import { useQuery } from '@tanstack/react-query';
import api, { ApiResponse } from '../axios';
import { ENDPOINTS } from '../endpoints';

// Dashboard types
export interface DashboardData {
  ticketStats: TicketStats;
  revenueStats: RevenueStats;
  lowStockAlerts: LowStockAlert[];
  recentTickets: TicketListItem[];
  urgentTickets: TicketListItem[];
  awaitingApproval: TicketListItem[];
  readyForPickup: TicketListItem[];
  technicianWorkload: TechnicianWorkload[];
}

export interface TicketStats {
  totalOpen: number;
  receivedToday: number;
  inDiagnosis: number;
  awaitingApproval: number;
  inRepair: number;
  readyForPickup: number;
  completedToday: number;
  deliveredToday: number;
  overdueTickets: number;
  averageRepairDays: number;
}

export interface RevenueStats {
  todayRevenue: MoneyValue;
  weekRevenue: MoneyValue;
  monthRevenue: MoneyValue;
  repairRevenue: MoneyValue;
  salesRevenue: MoneyValue;
  transactionsToday: number;
  growthPercent: number;
}

export interface MoneyValue {
  amount: number;
  currency: string;
  formatted: string;
}

export interface LowStockAlert {
  partId: string;
  partName: string;
  partSku: string;
  currentStock: number;
  minStock: number;
}

export interface TicketListItem {
  id: string;
  ticketNumber: string;
  customerName: string;
  customerPhone: string;
  deviceDescription: string;
  status: number;
  createdAt: string;
  dueDate?: string;
  technicianName?: string;
}

export interface TechnicianWorkload {
  technicianId: string;
  technicianName: string;
  openTickets: number;
  completedToday: number;
  capacity: number;
  utilizationPercent: number;
}

// Query keys
export const dashboardKeys = {
  all: ['dashboard'] as const,
  main: () => [...dashboardKeys.all, 'main'] as const,
  ticketStats: () => [...dashboardKeys.all, 'ticket-stats'] as const,
  revenueStats: () => [...dashboardKeys.all, 'revenue-stats'] as const,
  dailySummary: (date: string) => [...dashboardKeys.all, 'daily-summary', date] as const,
};

// Get main dashboard
export function useDashboard() {
  return useQuery({
    queryKey: dashboardKeys.main(),
    queryFn: async () => {
      const response = await api.get<ApiResponse<DashboardData>>(
        ENDPOINTS.DASHBOARD.STATS
      );
      return response.data.data!;
    },
    staleTime: 1000 * 60, // 1 minute
    refetchInterval: 1000 * 60 * 5, // Refresh every 5 minutes
  });
}

// Get ticket stats only
export function useTicketStats() {
  return useQuery({
    queryKey: dashboardKeys.ticketStats(),
    queryFn: async () => {
      const response = await api.get<ApiResponse<TicketStats>>(
        '/dashboard/ticket-stats'
      );
      return response.data.data!;
    },
    staleTime: 1000 * 30, // 30 seconds
  });
}

// Get revenue stats
export function useRevenueStats() {
  return useQuery({
    queryKey: dashboardKeys.revenueStats(),
    queryFn: async () => {
      const response = await api.get<ApiResponse<RevenueStats>>(
        '/dashboard/revenue-stats'
      );
      return response.data.data!;
    },
    staleTime: 1000 * 60, // 1 minute
  });
}

// Get daily summary
export function useDailySummary(date?: string) {
  const targetDate = date || new Date().toISOString().split('T')[0];

  return useQuery({
    queryKey: dashboardKeys.dailySummary(targetDate),
    queryFn: async () => {
      const response = await api.get<ApiResponse<unknown>>(
        '/dashboard/daily-summary',
        { params: { date: targetDate } }
      );
      return response.data.data!;
    },
  });
}
